import type {
    ChamferPropertyFieldAvailability,
    ChamferPropertyFieldKey,
    ChamferType,
    FilletType,
} from '@puppycad/cad-core';
import { getChamferPropertyDescriptor } from '@puppycad/cad-core';

/**
 * 圆角/倒角属性项可用性。
 */
export type BlendFieldAvailability = ChamferPropertyFieldAvailability;

/**
 * 圆角属性栏规格。
 */
export interface FilletPropertyPanelSpec {
    selectThroughFaces: BlendFieldAvailability;
    keepFeatures: BlendFieldAvailability;
    asymmetricRadiiFlip: BlendFieldAvailability;
    partialEdgeParameters: BlendFieldAvailability;
    profile: BlendFieldAvailability;
    roundCorners: BlendFieldAvailability;
    overflowType: BlendFieldAvailability;
    faceMode: BlendFieldAvailability;
    helpPoint: BlendFieldAvailability;
    continuousEdgesBlend: BlendFieldAvailability;
}

/**
 * 倒角属性栏规格。
 */
export type ChamferPropertyPanelSpec = Record<ChamferPropertyFieldKey, BlendFieldAvailability>;

const ENABLED: BlendFieldAvailability = 'enabled';
const DISABLED: BlendFieldAvailability = 'disabled';
const HIDDEN: BlendFieldAvailability = 'hidden';

/**
 * 圆角属性栏支持矩阵。
 */
export const FILLET_PROPERTY_PANEL_SPECS: Record<FilletType, FilletPropertyPanelSpec> = {
    constant: {
        selectThroughFaces: ENABLED,
        keepFeatures: ENABLED,
        asymmetricRadiiFlip: ENABLED,
        partialEdgeParameters: ENABLED,
        profile: HIDDEN,
        roundCorners: DISABLED,
        overflowType: ENABLED,
        faceMode: HIDDEN,
        helpPoint: HIDDEN,
        continuousEdgesBlend: HIDDEN,
    },
    variable: {
        selectThroughFaces: ENABLED,
        keepFeatures: HIDDEN,
        asymmetricRadiiFlip: ENABLED,
        partialEdgeParameters: HIDDEN,
        profile: DISABLED,
        roundCorners: HIDDEN,
        overflowType: DISABLED,
        faceMode: HIDDEN,
        helpPoint: HIDDEN,
        continuousEdgesBlend: DISABLED,
    },
    face: {
        selectThroughFaces: ENABLED,
        keepFeatures: HIDDEN,
        asymmetricRadiiFlip: HIDDEN,
        partialEdgeParameters: HIDDEN,
        profile: DISABLED,
        roundCorners: HIDDEN,
        overflowType: HIDDEN,
        faceMode: ENABLED,
        helpPoint: ENABLED,
        continuousEdgesBlend: HIDDEN,
    },
};

/**
 * 读取圆角属性栏规格。
 *
 * @param type 圆角类型。
 * @returns 属性栏规格。
 */
export function resolveFilletPropertyPanelSpec(type: FilletType): FilletPropertyPanelSpec {
    return FILLET_PROPERTY_PANEL_SPECS[type];
}

/**
 * 读取倒角属性栏规格。
 *
 * @param type 倒角类型。
 * @returns 属性栏规格。
 */
export function resolveChamferPropertyPanelSpec(type: ChamferType): ChamferPropertyPanelSpec {
    const descriptor = getChamferPropertyDescriptor(type);
    return Object.fromEntries(
        Object.entries(descriptor.fields).map(([key, field]) => [key, field.availability]),
    ) as ChamferPropertyPanelSpec;
}
